import { type ExprNode } from './node';

// ---------------------------------------------------------------------------
// Precedence levels (higher = binds tighter)
// ---------------------------------------------------------------------------

const PREC_ADD = 1;
const PREC_MUL = 2;
const PREC_POW = 3;
const PREC_ATOM = 4;

function precedenceOf(node: ExprNode): number {
  switch (node.kind) {
    case 'add':
      return PREC_ADD;
    case 'mul':
      return PREC_MUL;
    case 'pow':
      return PREC_POW;
    case 'num':
    case 'var':
    case 'func':
      return PREC_ATOM;
  }
}

// ---------------------------------------------------------------------------
// Core serialiser
// ---------------------------------------------------------------------------

/**
 * Serialise `node` to a human-readable infix string.
 *
 * `parentPrec` is the precedence level of the parent context — we parenthesise
 * `node` when its own precedence is strictly lower than the parent's.
 */
function stringifyInner(node: ExprNode, parentPrec: number): string {
  const s = stringifyNode(node);
  if (precedenceOf(node) < parentPrec) {
    return `(${s})`;
  }
  return s;
}

function stringifyNode(node: ExprNode): string {
  switch (node.kind) {
    // -------------------------------------------------------------------------
    // Atoms
    // -------------------------------------------------------------------------

    case 'num': {
      // Render integers without decimal point; negatives with leading '-'
      return String(node.value);
    }

    case 'var': {
      return node.name;
    }

    // -------------------------------------------------------------------------
    // add — join terms with ' + ', render negative-number children with ' - '
    // -------------------------------------------------------------------------

    case 'add': {
      if (node.children.length === 0) return '0';

      const parts: string[] = [];
      for (let i = 0; i < node.children.length; i++) {
        const child = node.children[i]!;
        if (i > 0 && child.kind === 'num' && child.value < 0) {
          // Render as ' - |value|' rather than ' + -value'
          parts.push(`- ${String(-child.value)}`);
        } else if (
          i > 0 &&
          child.kind === 'mul' &&
          child.children.length > 0 &&
          child.children[0]!.kind === 'num' &&
          (child.children[0] as { kind: 'num'; value: number }).value < 0
        ) {
          // Render as ' - rest' rather than ' + -n * rest'
          const coeff = (child.children[0] as { kind: 'num'; value: number }).value;
          const rest = child.children.slice(1);
          if (rest.length === 0) {
            parts.push(`- ${String(-coeff)}`);
          } else {
            const restNode =
              rest.length === 1 ? rest[0]! : { kind: 'mul' as const, children: rest };
            const absCoeff = -coeff;
            if (absCoeff === 1) {
              parts.push(`- ${stringifyInner(restNode, PREC_ADD)}`);
            } else {
              parts.push(`- ${String(absCoeff)} * ${stringifyInner(restNode, PREC_MUL)}`);
            }
          }
        } else if (i === 0) {
          parts.push(stringifyInner(child, PREC_ADD));
        } else {
          parts.push(`+ ${stringifyInner(child, PREC_ADD)}`);
        }
      }

      return parts.join(' ');
    }

    // -------------------------------------------------------------------------
    // mul — join factors with ' * '
    // -------------------------------------------------------------------------

    case 'mul': {
      if (node.children.length === 0) return '1';

      return node.children.map((child) => stringifyInner(child, PREC_MUL)).join(' * ');
    }

    // -------------------------------------------------------------------------
    // pow — right-associative: a^(b^c) needs no parens on the right
    // -------------------------------------------------------------------------

    case 'pow': {
      // Base: parenthesise if lower precedence than pow
      const baseStr = stringifyInner(node.base, PREC_POW);
      // Exp: for atoms no parens needed; for add/mul/pow we parenthesise
      // (using PREC_POW so that only atoms escape parens)
      const expStr = stringifyInner(node.exp, PREC_MUL);
      return `${baseStr}^${expStr}`;
    }

    // -------------------------------------------------------------------------
    // func — name(arg1, arg2, ...)
    // -------------------------------------------------------------------------

    case 'func': {
      const args = node.args.map((arg) => stringify(arg)).join(', ');
      return `${node.name}(${args})`;
    }
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Precedence-aware serialiser. Produces a human-readable infix string.
 * Integer exponents render without a decimal point (x^2, not x^2.0).
 */
export function stringify(node: ExprNode): string {
  return stringifyNode(node);
}
